import { zodResolver } from "@hookform/resolvers/zod";
import { PaymentAmountCard } from "Components/Customer/Payment/PaymentAmountCard";
import { PaymentLayout } from "Components/Customer/Payment/PaymentForms/PaymentLayout";
import {
  type StandardFormModel,
  standardPaymentSchema,
} from "Components/Customer/Payment/PaymentForms/schema";
import { TipSection } from "Components/Customer/Payment/PaymentForms/TipSection";
import { computeTipAmount } from "Components/Customer/Payment/PaymentForms/tipUtils";
import { usePayAccountWarmUp } from "Components/Customer/Payment/PaymentForms/usePayAccountWarmUp";
import { usePaymentSubmit } from "Components/Customer/Payment/PaymentForms/usePaymentSubmit";
import {
  type BankAccountListItem,
  TransactionStatus,
  type TransactionDetailDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";

type Props = {
  transactionDetails: TransactionDetailDto;
  bankAccounts: Array<BankAccountListItem>;
  defaultValues: Partial<StandardFormModel>;
};

export const StandardPaymentForm = ({
  transactionDetails,
  bankAccounts,
  defaultValues,
}: Props) => {
  const { submit, isSubmitting } = usePaymentSubmit();
  const { onBankAccountChange } = usePayAccountWarmUp(
    defaultValues.bankAccountPublicID,
  );
  const { t } = useResource();

  const {
    tipAmount,
    amount,
    currencyCode,
    transactionStatus,
    payPointTipsEnabled,
  } = transactionDetails;
  const initialTipDefaults =
    tipAmount > 0
      ? { tipMode: "custom" as const, customTipAmount: tipAmount }
      : { tipMode: "none" as const };

  const {
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    formState: { dirtyFields },
  } = useForm<StandardFormModel>({
    resolver: zodResolver(standardPaymentSchema),
    defaultValues: { ...defaultValues, ...initialTipDefaults }, // One-time snapshot on mount: later refetches of transactionDetails.tipAmount won't propagate into the form (useForm reads defaultValues only on first render).
    mode: "onChange",
  });

  const bankAccountPublicID = watch("bankAccountPublicID");
  const tipMode = watch("tipMode");
  const customTipAmount = watch("customTipAmount");

  const isTipDirty = Boolean(
    dirtyFields.tipMode || dirtyFields.customTipAmount,
  );
  const billAmount = amount - tipAmount;
  const computedTipAmount = computeTipAmount(
    tipMode,
    customTipAmount,
    billAmount,
  );
  const totalAmount = billAmount + computedTipAmount;

  const onSubmit = async ({ publicID }: StandardFormModel) => {
    const selectedBankAccount = bankAccounts.find(
      (b) => b.publicID === bankAccountPublicID,
    );
    if (!selectedBankAccount) {
      setError("bankAccountPublicID", {
        message: t(Resources.Common.Required),
      });
      return;
    }
    await submit({
      publicID,
      bankAccount: selectedBankAccount,
      tipAmount: computedTipAmount,
      currentTipAmount: tipAmount,
      isTipDirty,
      onPayError: (message) => setError("publicID", { message }),
    });
  };

  const wasTransactionCompleted =
    transactionStatus === TransactionStatus.Success;
  const wasTransactionCancelled =
    transactionStatus === TransactionStatus.Cancelled;
  const isPaymentDisabled =
    !bankAccountPublicID ||
    isSubmitting ||
    wasTransactionCompleted ||
    wasTransactionCancelled;

  return (
    <PaymentLayout
      transactionDetails={transactionDetails}
      bankAccounts={bankAccounts}
      control={control}
      totalAmount={totalAmount}
      currencyCode={currencyCode}
      isPaymentDisabled={isPaymentDisabled}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(onSubmit)}
      onBankAccountChange={onBankAccountChange}
    >
      <PaymentAmountCard
        billAmount={billAmount}
        tipAmount={computedTipAmount}
        currencyCode={currencyCode}
        arePayPointTipsEnabled={payPointTipsEnabled}
      />
      {payPointTipsEnabled && (
        <TipSection
          tipMode={tipMode}
          currencyCode={currencyCode}
          customTipAmount={customTipAmount}
          onTipModeChange={(mode) =>
            setValue("tipMode", mode, { shouldDirty: true })
          }
          onCustomTipAmountChange={(amount) =>
            setValue("customTipAmount", amount, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      )}
    </PaymentLayout>
  );
};
